import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRolesOverview } from '@/features/roles/hooks/useRoles';
import { normalizeLoginIdentifier } from '@/lib/loginIdentifier';
import * as repo from '../data/usersRepository';
import { useMinisterSuggestion, useUsersMutation } from '../hooks/useUsers';
import type { MinisterOption, TempCredentials } from '../types';
import { MinisterPicker } from './MinisterPicker';
import { emptyScope, isScopeComplete, type ScopeValue } from '../utils/scope';
import { ScopeFields } from './ScopeFields';

const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._-]{2,39}$/;
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Mode = 'minister' | 'staff';

interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (credentials: TempCredentials) => void;
}

export function CreateUserDialog({ open, onOpenChange, onCreated }: CreateUserDialogProps) {
  const { data: roles = [] } = useRolesOverview();
  const [mode, setMode] = useState<Mode>('minister');
  const [minister, setMinister] = useState<MinisterOption | null>(null);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [scope, setScope] = useState<ScopeValue>(emptyScope);
  const { data: suggestion, isFetching: loadingSuggestion } = useMinisterSuggestion(mode === 'minister' ? minister?.id ?? null : null);

  const activeRoles = useMemo(() => roles.filter((r) => r.is_active), [roles]);
  const ministerRole = activeRoles.find((r) => r.code === 'MINISTER');

  const reset = () => {
    setMode('minister'); setMinister(null); setName(''); setUsername(''); setContactEmail('');
    setRoleId(''); setScope(emptyScope);
  };
  useEffect(() => { if (open) reset(); }, [open]);

  // Picking a minister fills the name and the suggested username (the CI number, or the ADB code)
  useEffect(() => {
    if (!minister) return;
    setName(minister.full_name ?? '');
    if (ministerRole && !roleId) setRoleId(ministerRole.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minister, ministerRole?.id]);
  useEffect(() => { if (suggestion) setUsername(suggestion.suggested); }, [suggestion]);

  const create = useUsersMutation(repo.createUser);

  const normalized = normalizeLoginIdentifier(username);
  const usernameError =
    username && (normalized.includes('@') || !USERNAME_PATTERN.test(normalized))
      ? 'Entre 3 y 40 caracteres: letras minúsculas, números, punto, guion.'
      : null;
  const emailError = contactEmail && !EMAIL_PATTERN.test(contactEmail.trim()) ? 'El correo no es válido.' : null;
  const blockedByAccount = mode === 'minister' && !!suggestion?.has_account;
  const canSubmit =
    name.trim().length >= 3 && USERNAME_PATTERN.test(normalized) && !normalized.includes('@') &&
    !emailError && !!roleId && isScopeComplete(scope) && !blockedByAccount &&
    (mode === 'staff' || !!minister) && !create.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      {
        full_name: name.trim(),
        username: normalized,
        contact_email: contactEmail.trim() || undefined,
        minister_id: mode === 'minister' ? minister?.id : undefined,
        role_id: roleId,
        scope: scope.scope,
        district_id: scope.scope === 'DISTRICT' ? scope.districtId : undefined,
        region_id: scope.scope === 'REGION' ? scope.regionId : undefined,
      },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          onCreated({ name: name.trim(), username: result.username, password: result.temp_password });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Nuevo usuario</DialogTitle>
            <DialogDescription>
              Se crea con una contraseña temporal que verás una sola vez. Al entrar, deberá cambiarla.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={mode} onValueChange={(v) => { setMode(v as Mode); setMinister(null); setUsername(''); setName(''); }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="minister">Desde un ministro</TabsTrigger>
              <TabsTrigger value="staff">Personal (usuario libre)</TabsTrigger>
            </TabsList>
          </Tabs>

          {mode === 'minister' && (
            <div className="space-y-1.5">
              <Label>Ministro</Label>
              <MinisterPicker value={minister} onChange={setMinister} />
              {loadingSuggestion && <p className="text-xs text-muted-foreground">Calculando usuario…</p>}
              {suggestion?.has_account && (
                <Alert variant="destructive"><AlertDescription>Este ministro ya tiene una cuenta.</AlertDescription></Alert>
              )}
              {suggestion?.problem && !suggestion.has_account && (
                <Alert>
                  <AlertTriangle className="size-4" />
                  <AlertDescription>
                    {suggestion.problem}. Se propone su código ADB como usuario; el CI del ministro conviene corregirlo.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="new-name">Nombre completo</Label>
            <Input id="new-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-username">Usuario (para iniciar sesión)</Label>
              <Input id="new-username" value={username} className="font-mono" autoComplete="off" autoCapitalize="none"
                spellCheck={false} onChange={(e) => setUsername(e.target.value)}
                placeholder={mode === 'minister' ? 'CI del ministro' : 'p. ej. cajero.lapaz'} />
              {usernameError
                ? <p className="text-xs text-destructive">{usernameError}</p>
                : normalized && normalized !== username.trim().toLowerCase() &&
                  <p className="text-xs text-muted-foreground">Se guardará como <span className="font-mono">{normalized}</span></p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-email">Correo de contacto (opcional)</Label>
              <Input id="new-email" type="email" value={contactEmail} autoComplete="off" onChange={(e) => setContactEmail(e.target.value)} />
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-role">Rol</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger id="new-role"><SelectValue placeholder="Selecciona un rol" /></SelectTrigger>
              <SelectContent>
                {activeRoles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <ScopeFields value={scope} onChange={setScope} idPrefix="new" />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              Crear usuario
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
